import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Autocomplete,
  TextField,
  CircularProgress,
  Pagination,
  IconButton,
  Tooltip,
  Collapse,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Fab,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Business as BusinessIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIconAccordion,
} from '@mui/icons-material';
import { permissionsAPI, rolesAPI, usersAPI, departmentsAPI } from '../../services/api';
import { usePermissions } from '../../contexts/PermissionContext';

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`permissions-tabpanel-${index}`}
    aria-labelledby={`permissions-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
  </div>
);

const Permissions = () => {
  // Note: This component displays modules generically (all available modules and actions)
  // and allows unrestricted permission management across all companies
  // 
  // User Management: Uses getAllForPermissions() API to include both admin and merchant users
  // for comprehensive permission management across all user types
  
  const { hasPermission, refreshPermissions: refreshContextPermissions, autoRefreshEnabled, toggleAutoRefresh } = usePermissions();
  const [tabValue, setTabValue] = useState(0);
  const [moduleActions, setModuleActions] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [allRoles, setAllRoles] = useState([]);
  const [filteredRoles, setFilteredRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [rolePermissions, setRolePermissions] = useState({});
  const [userPermissions, setUserPermissions] = useState({});
  const [permissionSources, setPermissionSources] = useState({});
  const [userOverrides, setUserOverrides] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  
  // Searchable user dropdown states
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [userSearchPage, setUserSearchPage] = useState(1);
  const [userSearchTotalPages, setUserSearchTotalPages] = useState(1);
  const [userSearchTotal, setUserSearchTotal] = useState(0);
  const [userSearchOpen, setUserSearchOpen] = useState(false);

  const canView = hasPermission('Permissions', 'view');
  const canUpdate = hasPermission('Permissions', 'update');

  useEffect(() => {
    if (canView) {
      loadInitialData();
    }
  }, [canView]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [moduleActionsRes, departmentsRes, rolesRes] = await Promise.allSettled([
        permissionsAPI.getModuleActions(),
        departmentsAPI.getAll(),
        rolesAPI.getAll(),
        // Removed users loading - will be loaded on demand via search
      ]);

      if (moduleActionsRes.status === 'fulfilled') {
        setModuleActions(moduleActionsRes.value.data || []);
      }
      if (departmentsRes.status === 'fulfilled') {
        setDepartments(departmentsRes.value.data.data || []);
      }
      if (rolesRes.status === 'fulfilled') {
        const roles = rolesRes.value.data.data || [];
        setAllRoles(roles);
        setFilteredRoles(roles); // Initially show all roles
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
      setError('Failed to load permissions data');
    } finally {
      setLoading(false);
    }
  };

  // Debounced user search function
  const searchUsers = async (query, page = 1) => {
    try {
      setUserSearchLoading(true);
      const response = await usersAPI.getAllForPermissions({
        search: query,
        page: page,
        per_page: 20 // Limit results per page for better performance
      });
      
      setUserSearchResults(response.data.data || []);
      setUserSearchTotalPages(response.data.pages || 1);
      setUserSearchTotal(response.data.total || 0);
      setUserSearchPage(page);
    } catch (error) {
      console.error('Error searching users:', error);
      setError('Failed to search users');
      setUserSearchResults([]);
    } finally {
      setUserSearchLoading(false);
    }
  };

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (userSearchOpen) {
        searchUsers(userSearchQuery, 1);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [userSearchQuery, userSearchOpen]);

  // Load initial users when dropdown opens
  useEffect(() => {
    if (userSearchOpen && userSearchResults.length === 0) {
      searchUsers('', 1);
    }
  }, [userSearchOpen]);

  const loadRolePermissions = async (roleId) => {
    try {
      setLoading(true);
      const response = await permissionsAPI.getRolePermissions(roleId);
      setRolePermissions(response.data.permissions || {});
    } catch (error) {
      console.error('Error loading role permissions:', error);
      setError('Failed to load role permissions');
    } finally {
      setLoading(false);
    }
  };

  const loadUserPermissions = async (userId) => {
    try {
      setLoading(true);
      // Use the new management API that shows all actions with their states
      const response = await permissionsAPI.getUserPermissionsForManagement(userId);
      setUserPermissions(response.data.permissions || {});
      
      // Store the sources information for display
      setPermissionSources(response.data.sources || {});
      
      // Reset user overrides when loading new user
      setUserOverrides({});
    } catch (error) {
      console.error('Error loading user permissions:', error);
      setError('Failed to load user permissions');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    // Check if there are unsaved changes
    if (hasUnsavedChanges) {
      setShowUnsavedDialog(true);
      setPendingAction(() => () => {
        setTabValue(newValue);
        resetTabData();
      });
      return;
    }
    
    setTabValue(newValue);
    resetTabData();
  };

  const resetTabData = () => {
    setSelectedDepartment('');
    setSelectedRole('');
    setSelectedUser('');
    setRolePermissions({});
    setUserPermissions({});
    setPermissionSources({});
    setUserOverrides({});
    setHasUnsavedChanges(false);
  };

  const handleDepartmentChange = (departmentId) => {
    // Check if there are unsaved changes
    if (hasUnsavedChanges) {
      setShowUnsavedDialog(true);
      setPendingAction(() => () => {
        setSelectedDepartment(departmentId);
        setSelectedRole(''); // Reset role selection when department changes
        setRolePermissions({}); // Clear role permissions
        setHasUnsavedChanges(false);
        
        if (departmentId) {
          // Filter roles by selected department
          const rolesInDepartment = allRoles.filter(role => role.department_id === departmentId);
          setFilteredRoles(rolesInDepartment);
        } else {
          // Show all roles if no department selected
          setFilteredRoles(allRoles);
        }
      });
      return;
    }
    
    setSelectedDepartment(departmentId);
    setSelectedRole(''); // Reset role selection when department changes
    setRolePermissions({}); // Clear role permissions
    
    if (departmentId) {
      // Filter roles by selected department
      const rolesInDepartment = allRoles.filter(role => role.department_id === departmentId);
      setFilteredRoles(rolesInDepartment);
    } else {
      // Show all roles if no department selected
      setFilteredRoles(allRoles);
    }
  };

  const handleRoleChange = (roleId) => {
    // Check if there are unsaved changes
    if (hasUnsavedChanges) {
      setShowUnsavedDialog(true);
      setPendingAction(() => () => {
        setSelectedRole(roleId);
        setHasUnsavedChanges(false);
        if (roleId) {
          loadRolePermissions(roleId);
        } else {
          setRolePermissions({});
        }
      });
      return;
    }
    
    setSelectedRole(roleId);
    if (roleId) {
      loadRolePermissions(roleId);
    } else {
      setRolePermissions({});
    }
  };

  const handleUserChange = (user) => {
    const userId = user ? user.id : null;
    
    // Check if there are unsaved changes
    if (hasUnsavedChanges) {
      setShowUnsavedDialog(true);
      setPendingAction(() => () => {
        setSelectedUser(userId);
        setHasUnsavedChanges(false);
        if (userId) {
          loadUserPermissions(userId);
        } else {
          setUserPermissions({});
          setPermissionSources({});
        }
      });
      return;
    }
    
    setSelectedUser(userId);
    if (userId) {
      loadUserPermissions(userId);
    } else {
      setUserPermissions({});
      setPermissionSources({});
    }
  };

  // Handle pagination for user search
  const handleUserSearchPageChange = (event, page) => {
    searchUsers(userSearchQuery, page);
  };

  // Get selected user object for display
  const getSelectedUserObject = () => {
    if (!selectedUser) return null;
    return userSearchResults.find(user => user.id === selectedUser) || null;
  };

  const handleRolePermissionChange = (moduleId, actionId, checked) => {
    const moduleKey = String(moduleId);
    const actionKey = String(actionId);
    setRolePermissions(prev => ({
      ...prev,
      [moduleKey]: {
        ...prev[moduleKey],
        [actionKey]: checked
      }
    }));
    setHasUnsavedChanges(true);
  };

  const handleUserPermissionChange = (moduleId, actionId, checked) => {
    const moduleKey = String(moduleId);
    const actionKey = String(actionId);
    
      // Get the current effective permission and source
  const currentPermission = userPermissions[moduleKey] && userPermissions[moduleKey][actionKey] === true;
  const currentSource = permissionSources[moduleKey] && permissionSources[moduleKey][actionKey];
    
    // Update the display permissions
    setUserPermissions(prev => ({
      ...prev,
      [moduleKey]: {
        ...prev[moduleKey],
        [actionKey]: checked
      }
    }));
    
    // Track user-specific overrides - only track the specific changes
    if (currentSource === 'role') {
      // User is changing a role permission - create an override
      if (checked !== currentPermission) { // Only mark as unsaved if actually changed
        setHasUnsavedChanges(true);
      }
      
      if (checked !== true) { // Role permission is true, user wants false -> DENY override
        setUserOverrides(prev => ({
          ...prev,
          [moduleKey]: {
            ...prev[moduleKey],
            [actionKey]: false
          }
        }));
      } else {
        // User wants to keep role permission as-is, remove any existing override
        setUserOverrides(prev => {
          const newOverrides = { ...prev };
          if (newOverrides[moduleKey]) {
            delete newOverrides[moduleKey][actionKey];
            if (Object.keys(newOverrides[moduleKey]).length === 0) {
              delete newOverrides[moduleKey];
            }
          }
          return newOverrides;
        });
      }
    } else if (currentSource === 'user-override') {
      // User is changing an existing override
      setHasUnsavedChanges(true);
      
      if (checked) {
        // User wants to grant permission -> ALLOW override
        setUserOverrides(prev => ({
          ...prev,
          [moduleKey]: {
            ...prev[moduleKey],
            [actionKey]: true
          }
        }));
      } else {
        // User wants to deny permission -> DENY override
        setUserOverrides(prev => ({
          ...prev,
          [moduleKey]: {
            ...prev[moduleKey],
            [actionKey]: false
          }
        }));
      }
    } else if (currentSource === 'none') {
      // User is adding permission where none existed -> ALLOW override
      if (checked) {
        setHasUnsavedChanges(true);
        setUserOverrides(prev => ({
          ...prev,
          [moduleKey]: {
            ...prev[moduleKey],
            [actionKey]: true
          }
        }));
      } else {
        // User unchecked a 'none' permission, remove any override
        setUserOverrides(prev => {
          const newOverrides = { ...prev };
          if (newOverrides[moduleKey]) {
            delete newOverrides[moduleKey][actionKey];
            if (Object.keys(newOverrides[moduleKey]).length === 0) {
              delete newOverrides[moduleKey];
            }
          }
          return newOverrides;
        });
      }
    }
  };

  const saveRolePermissions = async () => {
    if (!selectedRole) return;

    try {
      setLoading(true);
      await permissionsAPI.updateRolePermissions(selectedRole, rolePermissions);
      setSuccess('Role permissions updated successfully');
      setHasUnsavedChanges(false);
      
      // Refresh permissions context to update sidebar and permission checks
      await refreshContextPermissions();
    } catch (error) {
      setError('Failed to update role permissions');
    } finally {
      setLoading(false);
    }
  };

  const saveUserPermissions = async () => {
    if (!selectedUser) return;

    try {
      setLoading(true);
      // Send only the user overrides to the backend instead of all permissions
      // This prevents clearing out other permissions when unchecking one
      await permissionsAPI.updateUserPermissions(selectedUser, userOverrides);
      setSuccess('User permissions updated successfully');
      setHasUnsavedChanges(false);
      
      // Reload user permissions to get the updated state
      await loadUserPermissions(selectedUser);
      
      // Refresh permissions context to update sidebar and permission checks
      await refreshContextPermissions();
    } catch (error) {
      setError('Failed to update user permissions');
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    // Check if there are unsaved changes
    if (hasUnsavedChanges) {
      setShowUnsavedDialog(true);
      setPendingAction(() => async () => {
        try {
          setLoading(true);
          await loadInitialData();
          // Reload current selections if any
          if (selectedRole) {
            await loadRolePermissions(selectedRole);
          }
          if (selectedUser) {
            await loadUserPermissions(selectedUser);
          }
          // Refresh user search results if dropdown is open
          if (userSearchOpen) {
            await searchUsers(userSearchQuery, userSearchPage);
          }
          setHasUnsavedChanges(false);
          setSuccess('Data refreshed successfully');
        } catch (error) {
          console.error('Error refreshing data:', error);
          setError('Failed to refresh data');
        } finally {
          setLoading(false);
        }
      });
      return;
    }
    
    try {
      setLoading(true);
      await loadInitialData();
      // Reload current selections if any
      if (selectedRole) {
        await loadRolePermissions(selectedRole);
      }
      if (selectedUser) {
        await loadUserPermissions(selectedUser);
      }
      // Refresh user search results if dropdown is open
      if (userSearchOpen) {
        await searchUsers(userSearchQuery, userSearchPage);
      }
      setSuccess('Data refreshed successfully');
    } catch (error) {
      console.error('Error refreshing data:', error);
      setError('Failed to refresh data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshPermissions = async () => {
    // Check if there are unsaved changes
    if (hasUnsavedChanges) {
      setShowUnsavedDialog(true);
      setPendingAction(() => async () => {
        try {
          setLoading(true);
          // First refresh the context permissions
          await refreshContextPermissions();
          
          // Then reload the current view data
          if (selectedRole) {
            await loadRolePermissions(selectedRole);
          }
          if (selectedUser) {
            await loadUserPermissions(selectedUser);
          }
          
          setHasUnsavedChanges(false);
          setSuccess('Permissions refreshed successfully');
        } catch (error) {
          console.error('Error refreshing permissions:', error);
          setError('Failed to refresh permissions');
        } finally {
          setLoading(false);
        }
      });
      return;
    }
    
    try {
      setLoading(true);
      // First refresh the context permissions
      await refreshContextPermissions();
      
      // Then reload the current view data
      if (selectedRole) {
        await loadRolePermissions(selectedRole);
      }
      if (selectedUser) {
        await loadUserPermissions(selectedUser);
      }
      
      setSuccess('Permissions refreshed successfully');
    } catch (error) {
      console.error('Error refreshing permissions:', error);
      setError('Failed to refresh permissions');
    } finally {
      setLoading(false);
    }
  };

  const handleUnsavedDialogConfirm = () => {
    setShowUnsavedDialog(false);
    if (pendingAction) {
      pendingAction();
      setPendingAction(null);
    }
  };

  const handleUnsavedDialogCancel = () => {
    setShowUnsavedDialog(false);
    setPendingAction(null);
  };

  const getPermissionStatus = (permissions, moduleId, actionId) => {
    // Convert IDs to strings since API returns string keys
    const moduleKey = String(moduleId);
    const actionKey = String(actionId);
    const hasPermission = permissions[moduleKey] && permissions[moduleKey][actionKey] === true;
    return hasPermission;
  };

  // Global accordion state manager - persists across all component instances
  const globalAccordionState = React.useRef({});
  const globalAccordionListeners = React.useRef(new Set());

  const PermissionMatrix = React.memo(({ permissions, onPermissionChange, type, sources }) => {
    console.log('PermissionMatrix rendered with type:', type, 'permissions keys:', Object.keys(permissions));
    
    // Note: Modules are displayed generically here (all available modules and actions)
    // and permission management is unrestricted across all companies
    
    // Get all unique actions across all modules for table headers
    const allActions = [...new Set(
      moduleActions.flatMap(module => 
        module.actions ? module.actions.map(action => action.action_name) : []
      )
    )];

    // Define priority actions to show first
    const priorityActions = ['create', 'delete', 'view', 'update', 'export'];
    
    // Sort actions with priority actions first, then alphabetically
    const sortedActions = allActions.sort((a, b) => {
      const aIndex = priorityActions.indexOf(a.toLowerCase());
      const bIndex = priorityActions.indexOf(b.toLowerCase());
      
      // If both are priority actions, sort by their priority order
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }
      
      // If only 'a' is priority, it comes first
      if (aIndex !== -1) {
        return -1;
      }
      
      // If only 'b' is priority, it comes first
      if (bIndex !== -1) {
        return 1;
      }
      
      // If neither is priority, sort alphabetically
      return a.localeCompare(b);
    });

    // State for collapsible rows (per-module accordion)
    const [expandedRows, setExpandedRows] = useState(() => {
      // Initialize from global state
      return globalAccordionState.current[type] || {};
    });
    const [pendingChanges, setPendingChanges] = useState({});
    const isCheckboxClick = React.useRef(false);
    const storageKey = React.useMemo(() => `permissions-accordion-${type}`, [type]);
    const isInitialMount = React.useRef(true);

    // Initialize accordion state from sessionStorage on mount
    useEffect(() => {
      if (isInitialMount.current) {
        isInitialMount.current = false;
        try {
          const saved = sessionStorage.getItem(storageKey);
          if (saved) {
            const parsed = JSON.parse(saved);
            if (parsed && typeof parsed === 'object') {
              globalAccordionState.current[type] = parsed;
              setExpandedRows(parsed);
              console.log('🔄 Loaded accordion state from sessionStorage:', parsed);
            }
          }
        } catch (e) {
          console.warn('Failed to load accordion state from sessionStorage', e);
        }
      }
    }, [storageKey, type]);

    // Save accordion state to sessionStorage and global state whenever it changes
    useEffect(() => {
      if (!isInitialMount.current) {
        try {
          globalAccordionState.current[type] = expandedRows;
          sessionStorage.setItem(storageKey, JSON.stringify(expandedRows));
          console.log('💾 Saved accordion state to sessionStorage and global state:', expandedRows);
        } catch (e) {
          console.warn('Failed to save accordion state to sessionStorage', e);
        }
      }
    }, [expandedRows, storageKey, type]);

    // Restore accordion state after permissions data changes (but not on initial mount)
    useEffect(() => {
      if (!isInitialMount.current) {
        const savedState = globalAccordionState.current[type];
        if (savedState && Object.keys(savedState).length > 0) {
          console.log('🔄 Restoring accordion state after data refresh:', savedState);
          setExpandedRows(savedState);
        }
      }
    }, [permissions, type]);

    // Define which columns to show initially (first 5)
    const VISIBLE_COLUMNS = 5;
    const visibleActions = sortedActions.slice(0, VISIBLE_COLUMNS);
    const hiddenActions = sortedActions.slice(VISIBLE_COLUMNS);

    const getPermissionSource = (moduleId, actionId) => {
      if (!sources) return null;
      const moduleKey = String(moduleId);
      const actionKey = String(actionId);
      return sources[moduleKey] && sources[moduleKey][actionKey] ? sources[moduleKey][actionKey] : null;
    };

    const getSourceColor = (source) => {
      switch (source) {
        case 'role': return 'primary';
        case 'user-override': return 'secondary';
        case 'none': return 'default';
        default: return 'default';
      }
    };

    const getSourceLabel = (source) => {
      switch (source) {
        case 'role': return 'Role';
        case 'user-override': return 'User';
        case 'none': return 'None';
        default: return 'Unknown';
      }
    };
    
    // Wrapper for permission change handler to track changes for display
    const handlePermissionChange = (moduleId, actionId, checked, event) => {
      console.log('Permission change triggered for module:', moduleId, 'action:', actionId, 'checked:', checked);
      console.log('Current expandedRows state:', expandedRows);
      
      // Only track changes for user permissions to avoid UI clutter in role management
      if (type === 'user') {
        const moduleKey = String(moduleId);
        const actionKey = String(actionId);
        const module = moduleActions.find(m => m.id === parseInt(moduleId));
        const action = module?.actions?.find(a => a.id === parseInt(actionId));
        
        if (module && action) {
          setPendingChanges(prev => ({
            ...prev,
            [`${moduleKey}-${actionKey}`]: {
              module: module.module_name,
              action: action.action_name,
              granted: checked
            }
          }));
        }
      }
      
      // Call the original handler
      onPermissionChange(moduleId, actionId, checked);
    };

    const toggleRowExpanded = (moduleId) => {
      console.log('=== TOGGLE ROW EXPANDED ===');
      console.log('Module ID:', moduleId);
      console.log('Current state:', expandedRows[moduleId]);
      console.log('Is checkbox click flag:', isCheckboxClick.current);
      
      // Don't toggle if this was triggered by a checkbox click
      if (isCheckboxClick.current) {
        console.log('❌ IGNORING toggle due to checkbox click');
        // Reset the flag after a small delay
        setTimeout(() => {
          isCheckboxClick.current = false;
          console.log('Reset checkbox click flag to false');
        }, 300);
        return;
      }
      
      console.log('✅ PROCEEDING with toggle');
      setExpandedRows(prev => {
        const newState = {
          ...prev,
          [moduleId]: !prev[moduleId]
        };
        console.log('New expanded state:', newState);
        return newState;
      });
    };

    const renderActionCell = (actionName, module) => {
      const moduleAction = module.actions && module.actions.find(a => a.action_name === actionName);
      if (!moduleAction) {
        return (
          <TableCell key={actionName} align="center">
            <Chip label="N/A" size="small" variant="outlined" />
          </TableCell>
        );
      }

      const hasPermission = getPermissionStatus(permissions, module.id, moduleAction.id);
      const source = getPermissionSource(module.id, moduleAction.id);
      const isUserOverride = source === 'user-override';
      const isFromRole = source === 'role';

      return (
        <TableCell key={actionName} align="center">
          <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
            <Checkbox
              checked={hasPermission}
              onChange={(e) => {
                console.log('=== TABLE CHECKBOX CLICKED ===');
                console.log('Module ID:', module.id);
                console.log('Action ID:', moduleAction.id);
                console.log('Checked:', e.target.checked);
                isCheckboxClick.current = true;
                handlePermissionChange(module.id, moduleAction.id, e.target.checked, e);
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
              disabled={!canUpdate}
              color={isUserOverride ? "secondary" : "primary"}
            />
            
            {/* Show permission source - only for user permissions */}
            {source && type === 'user' && (
              <Chip 
                label={getSourceLabel(source)} 
                size="small" 
                variant={isUserOverride ? "filled" : "outlined"}
                color={getSourceColor(source)}
                sx={{ fontSize: '0.7rem', height: '20px' }}
              />
            )}
            
            {/* Show action URL */}
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
              {moduleAction.action_url || 'N/A'}
            </Typography>
            
            {/* Show explanation for user permissions */}
            {type === 'user' && isFromRole && (
              <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.6rem', fontStyle: 'italic' }}>
                From role
              </Typography>
            )}
          </Box>
        </TableCell>
      );
    };

      return (
    <>
        {/* Column count indicator */}
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* <Typography variant="body2" color="text.secondary">
            Showing {visibleActions.length} of {sortedActions.length} permission columns
          </Typography> */}
          {/* {hiddenActions.length > 0 && (
              <Chip
              label={`${hiddenActions.length} additional columns available per module`}
                size="small"
              color="primary"
              variant="outlined"
            />
          )} */}
        </Box>
        
        {/* Custom Accordion for each module */}
        <Box sx={{ mt: 2 }}>
          {moduleActions.map((module) => {
            const isExpanded = expandedRows[module.id] || false;
            return (
              <Paper 
                key={module.id} 
                sx={{ 
                  mb: 2, 
                  overflow: 'hidden',
                  backgroundColor: 'grey.100',
                  boxShadow: 2,
                  '&:hover': {
                    boxShadow: 4,
                  }
                }}
              >
                {/* Header - Clickable to toggle */}
                <Box
                  onClick={() => {
                    console.log('=== HEADER CLICKED ===');
                    console.log('Module ID:', module.id);
                    console.log('Resetting checkbox flag to FALSE');
                    isCheckboxClick.current = false;
                    console.log('Checkbox flag is now:', isCheckboxClick.current);
                    toggleRowExpanded(module.id);
                  }}
                  sx={{
                    backgroundColor: isExpanded ? 'grey.200' : 'grey.100',
                    color: 'text.primary',
                    p: 2,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    borderBottom: isExpanded ? '1px solid' : 'none',
                    borderColor: 'grey.300',
                    '&:hover': {
                      backgroundColor: 'grey.200',
                    }
                  }}
                >
                  <SecurityIcon sx={{ color: 'inherit' }} />
                  <Box flexGrow={1}>
                    <Typography variant="h6" fontWeight="bold">
                          {module.module_name}
                        </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                          {module.actions ? module.actions.length : 0} actions available
                        </Typography>
                      </Box>
                  <Box display="flex" gap={1} alignItems="center">
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      {isExpanded ? 'Click to collapse' : 'Click to expand permissions'}
                    </Typography>
                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </Box>
                </Box>
                
                {/* Content - Only show when expanded */}
                {isExpanded && (
                  <Box 
                    onClick={(e) => {
                      console.log('=== GRID CONTAINER CLICKED ===');
                      e.stopPropagation();
                    }}
                    onMouseDown={(e) => {
                      console.log('=== GRID CONTAINER MOUSE DOWN ===');
                      e.stopPropagation();
                    }}
                    sx={{ 
                      p: 2,
                      backgroundColor: 'grey.50',
                      borderTop: '1px solid',
                      borderColor: 'grey.200'
                    }}
                  >
                    <Box 
                      onClick={(e) => {
                        console.log('=== INNER GRID CLICKED ===');
                        e.stopPropagation();
                      }}
                      onMouseDown={(e) => {
                        console.log('=== INNER GRID MOUSE DOWN ===');
                        e.stopPropagation();
                      }}
                      sx={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
                        gap: 2 
                      }}
                    >
                      {sortedActions.map((actionName) => {
                    const moduleAction = module.actions && module.actions.find(a => a.action_name === actionName);
                    if (!moduleAction) {
                      return (
                            <Paper 
                              key={actionName} 
                              onClick={(e) => {
                                console.log('=== N/A CARD CLICKED ===');
                                e.stopPropagation();
                              }}
                              onMouseDown={(e) => {
                                console.log('=== N/A CARD MOUSE DOWN ===');
                                e.stopPropagation();
                              }}
                              sx={{ 
                                p: 2, 
                                backgroundColor: 'grey.50',
                                border: '1px solid',
                                borderColor: 'grey.200',
                                boxShadow: 1,
                                '&:hover': {
                                  boxShadow: 2,
                                }
                              }}
                            >
                              <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={2}>
                                <Box flexGrow={1} minWidth={0}>
                                  <Typography 
                                    variant="subtitle2" 
                                    fontWeight="bold" 
                                    color="text.secondary" 
                                    sx={{ 
                                      textTransform: 'capitalize',
                                      wordBreak: 'break-word'
                                    }}
                                  >
                                    {actionName.replace(/_/g, ' ')}
                                  </Typography>
                                </Box>
                          <Chip label="N/A" size="small" variant="outlined" />
                              </Box>
                            </Paper>
                      );
                    }

                    const hasPermission = getPermissionStatus(permissions, module.id, moduleAction.id);
                    const source = getPermissionSource(module.id, moduleAction.id);
                    const isUserOverride = source === 'user-override';
                    const isFromRole = source === 'role';

                    return (
                            <Paper 
                              key={actionName} 
                              onClick={(e) => {
                                console.log('=== PERMISSION CARD CLICKED ===');
                                e.stopPropagation();
                              }}
                              onMouseDown={(e) => {
                                console.log('=== PERMISSION CARD MOUSE DOWN ===');
                                e.stopPropagation();
                              }}
                              sx={{ 
                                p: 2, 
                                border: '1px solid', 
                                borderColor: 'grey.300',
                                backgroundColor: 'white',
                                boxShadow: 1,
                                '&:hover': {
                                  boxShadow: 2,
                                }
                              }}
                            >
                            <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={2}>
                              {/* Action name on the left */}
                              <Box flexGrow={1} minWidth={0}>
                                <Typography 
                                  variant="subtitle2" 
                                  fontWeight="bold" 
                                  sx={{ 
                                    textTransform: 'capitalize',
                                    wordBreak: 'break-word'
                                  }}
                                >
                                  {actionName.replace(/_/g, ' ')}
                                </Typography>
                                {/* Show action URL */}
                                <Typography 
                                  variant="caption" 
                                  color="text.secondary" 
                                  sx={{ 
                                    fontSize: '0.65rem',
                                    wordBreak: 'break-word',
                                    display: 'block'
                                  }}
                                >
                                  {moduleAction.action_url || 'N/A'}
                                </Typography>
                              </Box>
                              
                              {/* Checkbox and status on the right */}
                              <Box 
                                display="flex" 
                                flexDirection="column" 
                                alignItems="center" 
                                gap={1}
                                onClick={(e) => {
                                  console.log('=== CHECKBOX CONTAINER CLICKED ===');
                                  e.stopPropagation();
                                }}
                              >
                          <Checkbox
                            checked={hasPermission}
                                  onChange={(e) => {
                                    console.log('=== CHECKBOX CLICKED ===');
                                    console.log('Module ID:', module.id);
                                    console.log('Action ID:', moduleAction.id);
                                    console.log('Checked:', e.target.checked);
                                    console.log('Setting checkbox flag to TRUE');
                                    isCheckboxClick.current = true;
                                    console.log('Checkbox flag is now:', isCheckboxClick.current);
                                    handlePermissionChange(module.id, moduleAction.id, e.target.checked, e);
                                  }}
                                  onClick={(e) => {
                                    console.log('=== CHECKBOX ONCLICK ===');
                                    e.stopPropagation();
                                  }}
                            disabled={!canUpdate}
                            color={isUserOverride ? "secondary" : "primary"}
                                  size="large"
                          />
                          
                          {/* Show permission source - only for user permissions */}
                          {source && type === 'user' && (
                            <Chip 
                              label={getSourceLabel(source)} 
                              size="small" 
                              variant={isUserOverride ? "filled" : "outlined"}
                              color={getSourceColor(source)}
                              sx={{ fontSize: '0.7rem', height: '20px' }}
                            />
                          )}
                          
                          {/* Show explanation for user permissions */}
                          {type === 'user' && isFromRole && (
                            <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.6rem', fontStyle: 'italic' }}>
                              From role
                            </Typography>
                          )}
                        </Box>
                            </Box>
                          </Paper>
                    );
                  })}
                    </Box>
                  </Box>
                )}
              </Paper>
            );
          })}
        </Box>
      </>
    );
  });
  // Fix the issue with Access Denied on page refresh
  useEffect(() => {
    // Add event listener for page refresh
    const handleBeforeUnload = (e) => {
      // This prevents the "Access Denied" issue by ensuring we don't
      // lose permissions state on page refresh
      if (hasUnsavedChanges) {
        // Standard way of showing a confirmation dialog before refresh
        e.preventDefault();
        e.returnValue = ''; // Chrome requires returnValue to be set
        return ''; // This text will be displayed in some browsers
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  if (!canView) {
    return (
      <Box p={3}>
        <Alert severity="error">
          You don't have permission to view permissions.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">
          Permissions Management
        </Typography>
        
        <Box display="flex" gap={1} alignItems="center">
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={refreshData}
            disabled={loading}
          >
            Refresh Data
          </Button>
          
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<SecurityIcon />}
            onClick={handleRefreshPermissions}
            disabled={loading}
          >
            Refresh Permissions
          </Button>

          <FormControlLabel
            control={
              <Switch
                checked={autoRefreshEnabled}
                onChange={toggleAutoRefresh}
                color="primary"
              />
            }
            label="Auto-refresh (2min)"
            sx={{ ml: 2 }}
          />
        </Box>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          <strong>Dynamic Actions:</strong> Custom actions added to modules will automatically appear here. 
          Use the refresh button above to reload if you've just added new actions.
          <br />
   
        </Typography>
      </Alert>

      <Paper sx={{ width: '100%' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<GroupIcon />} label="Role Permissions" />
          <Tab icon={<PersonIcon />} label="User Permissions" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Select Department & Role
                  </Typography>
                  
                  {/* Department, Role Selection and Save Button in same row */}
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth>
                        <InputLabel>Department</InputLabel>
                        <Select
                          value={selectedDepartment}
                          onChange={(e) => handleDepartmentChange(e.target.value)}
                          label="Department"
                        >
                          <MenuItem value="">
                            <em>All Departments</em>
                          </MenuItem>
                          {departments.map((dept) => (
                            <MenuItem key={dept.id} value={dept.id}>
                              {dept.department_name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth>
                        <InputLabel>Role</InputLabel>
                        <Select
                          value={selectedRole}
                          onChange={(e) => handleRoleChange(e.target.value)}
                          label="Role"
                          disabled={!selectedDepartment}
                        >
                          <MenuItem value="">
                            <em>Select a role</em>
                          </MenuItem>
                          {filteredRoles.map((role) => (
                            <MenuItem key={role.id} value={role.id}>
                              <Box display="flex" alignItems="center">
                                <GroupIcon sx={{ mr: 1, fontSize: 'small' }} />
                                {role.role_name}
                                <Chip 
                                  label={role.department_name} 
                                  size="small" 
                                  variant="outlined" 
                                  sx={{ ml: 1 }} 
                                />
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                      {selectedRole && (
                        <Button
                          variant="contained"
                          startIcon={<SaveIcon />}
                          onClick={saveRolePermissions}
                          disabled={!canUpdate || loading}
                          fullWidth
                        >
                          Save Permissions
                        </Button>
                      )}
                    </Grid>
                  </Grid>
                  
                  {!selectedDepartment && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Please select a department first to see available roles.
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12}>
              {selectedRole ? (
                <Box>
                  {/* Role Information Header */}
                  <Paper sx={{ p: 2, mb: 2, backgroundColor: 'primary.light', color: 'primary.contrastText' }}>
                    <Typography variant="h6" gutterBottom>
                      Managing Permissions for:
                    </Typography>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Chip 
                        icon={<SecurityIcon />}
                        label={`Department: ${departments.find(d => d.id === selectedDepartment)?.department_name || 'N/A'}`}
                        variant="filled"
                        color="secondary"
                      />
                      <Chip 
                        icon={<GroupIcon />}
                        label={`Role: ${filteredRoles.find(r => r.id === selectedRole)?.role_name || 'N/A'}`}
                        variant="filled"
                        color="secondary"
                      />
                    </Box>
                  </Paper>
                  
                  <PermissionMatrix
                    permissions={rolePermissions}
                    onPermissionChange={handleRolePermissionChange}
                    type="role"
                    sources={null}
                  />
                </Box>
              ) : (
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary">
                    {!selectedDepartment 
                      ? 'Select a department first, then choose a role to manage its permissions'
                      : 'Select a role to manage its permissions'
                    }
                  </Typography>
                </Paper>
              )}
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Select User
                  </Typography>
                  
                  {/* User Selection and Save Button in same row */}
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={8}>
                      <Autocomplete
                        value={getSelectedUserObject()}
                        onChange={(event, newValue) => handleUserChange(newValue)}
                        onOpen={() => setUserSearchOpen(true)}
                        onClose={() => setUserSearchOpen(false)}
                        inputValue={userSearchQuery}
                        onInputChange={(event, newInputValue) => setUserSearchQuery(newInputValue)}
                        options={userSearchResults}
                        getOptionLabel={(option) => option ? `${option.name} (${option.email})` : ''}
                        isOptionEqualToValue={(option, value) => option?.id === value?.id}
                        loading={userSearchLoading}
                        noOptionsText={userSearchLoading ? "Loading..." : "No users found"}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Search and Select User"
                            placeholder="Type to search users..."
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {userSearchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                        renderOption={(props, option) => (
                          <Box component="li" {...props}>
                            <Box display="flex" alignItems="center" width="100%">
                              <PersonIcon sx={{ mr: 1, fontSize: 'small', color: 'text.secondary' }} />
                              <Box flexGrow={1}>
                                <Typography variant="body2" fontWeight="medium">
                                  {option.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {option.email}
                                </Typography>
                              </Box>
                              {option.department_name && (
                                <Chip 
                                  label={option.department_name} 
                                  size="small" 
                                  variant="outlined" 
                                  sx={{ ml: 1, fontSize: '0.7rem' }}
                                />
                              )}
                            </Box>
                          </Box>
                        )}
                        ListboxProps={{
                          style: { maxHeight: 300, overflow: 'auto' }
                        }}
                      />
                      
                      {/* Pagination for user search results */}
                      {userSearchTotalPages > 1 && (
                        <Box display="flex" justifyContent="center" mt={1}>
                          <Pagination
                            count={userSearchTotalPages}
                            page={userSearchPage}
                            onChange={handleUserSearchPageChange}
                            size="small"
                            color="primary"
                            showFirstButton
                            showLastButton
                          />
                        </Box>
                      )}
                      
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                      {selectedUser && (
                        <Button
                          variant="contained"
                          startIcon={<SaveIcon />}
                          onClick={saveUserPermissions}
                          disabled={!canUpdate || loading}
                          fullWidth
                        >
                          Save User Permissions
                        </Button>
                      )}
                    </Grid>
                  </Grid>
                  
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12}>
              {selectedUser ? (
                <Box>
                  {/* User Information Header */}
                  <Paper sx={{ p: 2, mb: 2, backgroundColor: 'secondary.light', color: 'secondary.contrastText' }}>
                    <Typography variant="h6" gutterBottom>
                      Managing User-Specific Permissions for:
                    </Typography>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Chip 
                        icon={<PersonIcon />}
                        label={`User: ${getSelectedUserObject()?.name || 'N/A'}`}
                        variant="filled"
                        color="primary"
                      />
                      <Chip 
                        icon={<BusinessIcon />}
                        label={`Email: ${getSelectedUserObject()?.email || 'N/A'}`}
                        variant="filled"
                        color="primary"
                      />
                      {getSelectedUserObject()?.department_name && (
                        <Chip 
                          icon={<GroupIcon />}
                          label={`Department: ${getSelectedUserObject()?.department_name}`}
                          variant="filled"
                          color="secondary"
                        />
                      )}
                    </Box>
                  </Paper>
                  
                  {/* Pending User Overrides container commented out to reduce scrolling (TC_PER_04)
                  {Object.keys(userOverrides).length > 0 && (
                    <Paper sx={{ p: 2, mb: 2, backgroundColor: 'warning.light' }}>
                      <Typography variant="h6" gutterBottom>
                        Pending User Overrides ({Object.keys(userOverrides).reduce((count, moduleId) => count + Object.keys(userOverrides[moduleId]).length, 0)})
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        These changes will be applied when you click "Save User Permissions":
                      </Typography>
                      {Object.entries(userOverrides).map(([moduleId, actions]) =>
                        Object.entries(actions).map(([actionId, granted]) => {
                          const module = moduleActions.find(m => m.id === parseInt(moduleId));
                          const action = module?.actions?.find(a => a.id === parseInt(actionId));
                          return (
                            <Chip
                              key={`${moduleId}-${actionId}`}
                              label={`${module?.module_name || `Module ${moduleId}`} → ${action?.action_name || `Action ${actionId}`}: ${granted ? 'ALLOW' : 'DENY'}`}
                              color={granted ? 'success' : 'error'}
                              size="small"
                              sx={{ mr: 1, mb: 1 }}
                            />
                          );
                        })
                      )}
                    </Paper>
                  )}
                  */}
                  
                  <PermissionMatrix
                    permissions={userPermissions}
                    onPermissionChange={handleUserPermissionChange}
                    type="user"
                    sources={permissionSources}
                  />
                </Box>
              ) : (
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary">
                    Select a user to manage their specific permissions
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    User-specific permissions override role-based permissions
                  </Typography>
                </Paper>
              )}
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>

      {/* Unsaved Changes Dialog */}
      <Dialog
        open={showUnsavedDialog}
        onClose={handleUnsavedDialogCancel}
      >
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved permission changes. Do you want to discard these changes?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUnsavedDialogCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleUnsavedDialogConfirm} color="error" autoFocus>
            Discard Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fixed Save Button - Bottom Right */}
      {(selectedRole || selectedUser) && (
        <Tooltip 
          title={tabValue === 0 ? "Save Role Permissions" : "Save User Permissions"} 
          placement="left"
        >
          <Fab
            color="primary"
            aria-label="save permissions"
            onClick={tabValue === 0 ? saveRolePermissions : saveUserPermissions}
            disabled={!canUpdate || loading}
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              zIndex: 1000,
              boxShadow: 3,
              '&:hover': {
                boxShadow: 6,
              }
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : <SaveIcon />}
          </Fab>
        </Tooltip>
      )}
    </Box>
  );
};

export default Permissions; 